'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { useUserData } from '@/context/UserDataContext';
import type { AlmanacData } from '@/lib/almanac';

const NOAA_SOURCE = 'NOAA 30-year climate normals (1991-2020)';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS_BEFORE_MONTH = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

/** "April 22" -> fraction of the year, for the frost-free timeline. */
function yearFraction(label: string | null): number | null {
  if (!label) return null;
  const [monthName, dayText] = label.split(' ');
  const monthIndex = MONTH_NAMES.indexOf(monthName);
  const day = parseInt(dayText ?? '', 10);
  if (monthIndex === -1 || !Number.isFinite(day)) return null;
  return (DAYS_BEFORE_MONTH[monthIndex] + day) / 365;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-sand p-5">
      <h2 className="font-heading text-[20px] text-dark-text leading-tight">{title}</h2>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="font-body text-[14px] text-sage">{label}</span>
      <span className="font-body text-[14px] text-dark-text text-right">{value}</span>
    </div>
  );
}

function Unavailable({ children }: { children: React.ReactNode }) {
  return <p className="font-body text-[14px] text-sage leading-relaxed">{children}</p>;
}

// Endpoints of the US range, from the same 1991-2020 annual normals:
// Duluth MN is the cool end, Brownsville TX and Miami FL the hot end.
const GDD_SCALE_MIN = 1500;
const GDD_SCALE_MAX = 9800;

function growingSeasonBand(total: number): string {
  if (total < 2000) return 'a short, cool season';
  if (total < 3000) return 'a cool season';
  if (total < 4500) return 'a moderate season';
  if (total < 6500) return 'a warm season';
  return 'a long, hot season';
}

function GrowingDegreeDays({ total }: { total: number }) {
  const position = Math.min(100, Math.max(0,
    ((total - GDD_SCALE_MIN) / (GDD_SCALE_MAX - GDD_SCALE_MIN)) * 100,
  ));

  return (
    <div className="pt-1">
      <Row label="Growing degree days per year" value={total.toLocaleString('en-US')} />
      <p className="font-body text-[14px] text-dark-text mt-1">
        {growingSeasonBand(total).replace(/^a /, 'A ')}
      </p>

      <div className="relative h-3 w-full rounded-full bg-light-sage mt-3">
        <div
          className="absolute top-1/2 w-3 h-3 rounded-full bg-deep-green border-2 border-white"
          style={{ left: `${position}%`, transform: 'translate(-50%, -50%)' }}
        />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="font-body text-[12px] text-sage">Cooler · Duluth 1,660</span>
        <span className="font-body text-[12px] text-sage">Brownsville 8,900 · Hotter</span>
      </div>

      <p className="font-body text-[13px] text-sage leading-relaxed mt-3">
        Not a count of days, but a measure of accumulated warmth. Each day adds however many
        degrees its average temperature sits above 50{'°'}F: a 70{'°'}F day adds 20, a 55{'°'}F
        day adds 5, a chilly day adds nothing. Long-season crops are sometimes rated in these
        units, so the total describes how much heat your season has to offer.
      </p>
    </div>
  );
}

function FrostTimeline({ frost }: { frost: AlmanacData['frost'] }) {
  const start = yearFraction(frost.avgLastSpringFrost);
  const end = yearFraction(frost.avgFirstFallFrost);
  if (start === null || end === null || end <= start) return null;

  return (
    <div className="pt-1">
      <div className="relative h-3 w-full rounded-full bg-light-sage overflow-hidden">
        <div
          className="absolute inset-y-0 bg-deep-green"
          style={{ left: `${start * 100}%`, width: `${(end - start) * 100}%` }}
        />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="font-body text-[12px] text-sage">Jan</span>
        <span className="font-body text-[12px] text-sage">Jul</span>
        <span className="font-body text-[12px] text-sage">Dec</span>
      </div>
      <p className="font-body text-[13px] text-sage mt-2 leading-relaxed">
        The shaded band is your average frost-free window, {frost.avgLastSpringFrost} to{' '}
        {frost.avgFirstFallFrost}.
      </p>
    </div>
  );
}

export default function AlmanacContent() {
  const { user, loading: authLoading } = useAuth();
  const { almanacData, almanacLoading, loadAlmanac, zip, isLoaded } = useUserData();
  const requested = useRef(false);

  useEffect(() => {
    if (!user || requested.current) return;
    requested.current = true;
    void loadAlmanac();
  }, [user, loadAlmanac]);

  if (authLoading || !isLoaded) return null;

  if (!user) {
    return (
      <div className="space-y-6">
        <Header city={null} state={null} />
        <div className="bg-white rounded-xl border border-sand p-5">
          <p className="font-body text-[15px] text-sage leading-relaxed">
            Sign in to see the climate, frost, and soil record for your location.
          </p>
          <Link
            href="/login"
            className="inline-block mt-3 font-body font-bold text-[14px] text-deep-green hover:underline"
          >
            Sign in →
          </Link>
        </div>
      </div>
    );
  }

  if (!zip) {
    return (
      <div className="space-y-6">
        <Header city={null} state={null} />
        <div className="bg-white rounded-xl border border-sand p-5">
          <p className="font-body text-[15px] text-sage leading-relaxed">
            Set your zip code and your almanac fills in with the climate, frost, and soil record
            for your corner of the map.
          </p>
          <Link
            href="/dashboard"
            className="inline-block mt-3 font-body font-bold text-[14px] text-deep-green hover:underline"
          >
            Set your zip →
          </Link>
        </div>
      </div>
    );
  }

  if (!almanacData) {
    return (
      <div className="space-y-6">
        <Header city={null} state={null} />
        <div className="bg-white rounded-xl border border-sand p-5">
          <p className="font-body text-[15px] text-sage leading-relaxed">
            {almanacLoading
              ? 'Reading your land’s history...'
              : 'Your almanac could not be loaded just now. It will fill in automatically next time you visit.'}
          </p>
        </div>
      </div>
    );
  }

  const { location, zone, climate, frost, soil, sectionsLoaded } = almanacData;
  const [minLow, maxLow] = zone.minWinterTempF;

  return (
    <div className="space-y-6">
      <Header city={location.city} state={location.state} />

      <Card title="Your Growing Zone">
        <p className="font-heading text-[32px] text-deep-green leading-none">
          Zone {zone.hardinessZone}
        </p>
        <Row label="Average winter minimum" value={`${minLow}°F to ${maxLow}°F`} />
        <p className="font-body text-[14px] text-sage leading-relaxed">
          Perennials rated to Zone {zone.hardinessZone} or hardier come through a typical winter
          in the ground here.
        </p>

        {sectionsLoaded.frost && frost.avgLastSpringFrost ? (
          <>
            <div className="pt-2 space-y-3">
              <Row label="Average last spring frost" value={frost.avgLastSpringFrost} />
              <Row label="Average first fall frost" value={frost.avgFirstFallFrost} />
              <Row
                label="Growing season"
                value={frost.growingSeasonDays ? `${frost.growingSeasonDays} days` : null}
              />
            </div>
            <FrostTimeline frost={frost} />
            <p className="font-body text-[12px] text-sage leading-relaxed pt-1">
              Frost dates from {NOAA_SOURCE}. Planting dates elsewhere in Garden use zone-based
              averages, so the two may differ by a week or so.
            </p>
          </>
        ) : (
          <Unavailable>
            Frost normals are not available for your nearest weather station.
          </Unavailable>
        )}
      </Card>

      <Card title="Your Climate">
        {sectionsLoaded.climate ? (
          <>
            {climate.classification && (
              <p className="font-body text-[15px] text-dark-text leading-relaxed">
                {climate.classification}
              </p>
            )}
            <Row
              label="Average annual rainfall"
              value={climate.avgAnnualRainfallInches !== null
                ? `${climate.avgAnnualRainfallInches} inches`
                : null}
            />
            <Row label="Wettest month" value={climate.wettestMonth} />
            <Row label="Driest month" value={climate.driestMonth} />
            <Row
              label="Average annual snowfall"
              value={climate.avgAnnualSnowfallInches !== null
                ? `${climate.avgAnnualSnowfallInches} inches`
                : null}
            />
            <Row
              label="Average summer high"
              value={climate.avgSummerHighF !== null ? `${climate.avgSummerHighF}°F` : null}
            />
            <Row
              label="Average winter low"
              value={climate.avgWinterLowF !== null ? `${climate.avgWinterLowF}°F` : null}
            />
            <Row label="Hottest month" value={climate.hottestMonth} />
            <Row label="Coldest month" value={climate.coldestMonth} />
            {climate.growingDegreeDays !== null && (
              <GrowingDegreeDays total={climate.growingDegreeDays} />
            )}
            <p className="font-body text-[12px] text-sage leading-relaxed pt-1">
              Source: {NOAA_SOURCE}.
            </p>
          </>
        ) : (
          <Unavailable>
            Climate normals could not be loaded for your nearest weather station.
          </Unavailable>
        )}
      </Card>

      <Card title="Your Soil">
        {soil ? (
          <>
            <Row label="Dominant soil" value={soil.dominantType} />
            <Row label="Texture" value={soil.texture} />
            <Row label="Drainage" value={soil.drainageClass} />
            <Row label="pH tendency" value={soil.phTendency} />
            <Row label="Organic matter" value={soil.organicMatter} />
            <p className="font-body text-[14px] text-dark-text leading-relaxed pt-1">
              {soil.amendmentNote}
            </p>
            <p className="font-body text-[12px] text-sage leading-relaxed pt-1">
              From the USDA soil survey at the approximate center of your zip code. Soils vary
              over short distances, so your own beds may differ.
            </p>
          </>
        ) : (
          <Unavailable>
            Detailed soil data is not available for your exact location. The USDA soil survey has
            not mapped this area.{' '}
            <a
              href="https://websoilsurvey.nrcs.usda.gov/app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-deep-green underline underline-offset-2 hover:opacity-80"
            >
              USDA Web Soil Survey
            </a>
          </Unavailable>
        )}
      </Card>
    </div>
  );
}

function Header({ city, state }: { city: string | null; state: string | null }) {
  return (
    <div>
      <h1 className="font-heading text-[28px] text-dark-text leading-tight">Your Almanac</h1>
      <p className="font-body text-[16px] text-sage mt-1">
        {city && state
          ? `Everything your land can tell you, ${city}, ${state}`
          : 'Everything your land can tell you'}
      </p>
    </div>
  );
}
